import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hvApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import {
  Loader2, Briefcase, Users, Palette, FileSignature, CreditCard,
  Save, CheckCircle, Mail, Trash2, Copy, ExternalLink, Plus,
  ArrowRight, Star, Zap, ChevronLeft, Edit2, Image as ImageIcon,
  Phone, Shield, User, Sliders, Download, Ticket, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import HVNav from '../components/HVNav';
import HVFooter from '../components/HVFooter';

// ─── Constants ────────────────────────────────────────────────────────────────
const LS_BANNERS    = 'immo_banners_v1';
const LS_SIGNATURES = 'immo_signatures_v1';

// Email-safe fonts only — web fonts get stripped by Outlook/Gmail
const EMAIL_FONTS = [
  { label: 'Arial (Standard)',       value: 'Arial, Helvetica, sans-serif' },
  { label: 'Verdana (Gut lesbar)',   value: 'Verdana, Geneva, sans-serif' },
  { label: 'Trebuchet MS (Modern)',  value: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: 'Tahoma (Kompakt)',       value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Georgia (Klassisch)',    value: "Georgia, 'Times New Roman', serif" },
  { label: 'Times New Roman (Formal)', value: "'Times New Roman', Times, serif" },
  { label: 'Courier New (Technisch)', value: "'Courier New', Courier, monospace" },
];

const LOGO_SIZES  = { small: 24, medium: 36, large: 52 };
const DIVIDER_PX  = { thin: 1, medium: 2, thick: 3 };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function loadLS(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) || fallback; }
  catch { return fallback; }
}
function saveLS(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}
function uid() { return Math.random().toString(36).slice(2, 10); }

// ─── Default objects ──────────────────────────────────────────────────────────
const defaultBanner = () => ({
  id: uid(),
  name: 'Neuer Banner',
  headline: 'Wie zufrieden sind Sie mit uns?',
  subtext: 'Ihre Meinung hilft uns, besser zu werden.',
  cta: 'Jetzt bewerten',
  linkMode: 'auto',
  customLink: '',
  imageUrl: '',
  logoUrl: '',
  logoSize: 36,           // px
  logoPosition: 'above',  // above | inline-left | inline-right
  bgColor: '#1a56db',
  textColor: '#ffffff',
  // Corporate Design
  fontFamily: 'Arial, Helvetica, sans-serif',
  textAlign: 'left',
  borderRadius: '12',
  buttonStyle: 'filled',  // filled | outlined
});

const defaultSignature = (user) => ({
  id: uid(),
  name: 'Neue Signatur',
  personName: user?.tenant?.public_profile?.display_name || '',
  personTitle: '',
  personTagline: '',
  companyName: user?.tenant?.public_profile?.legal_name || user?.tenant?.public_profile?.display_name || '',
  services: '',
  phone: user?.tenant?.public_profile?.phone || '',
  email: user?.email || '',
  website: user?.tenant?.public_profile?.website || '',
  address: [user?.tenant?.public_profile?.address_street, user?.tenant?.public_profile?.address_city].filter(Boolean).join(', '),
  calendarLink: '',
  calendarLinkText: 'Termin vereinbaren',
  accentColor: user?.tenant?.public_profile?.brand_color || '#1a56db',
  logoUrl: '',
  photoUrl: '',
  showRatingButton: true,
  ratingButtonText: 'Jetzt bewerten',
  bannerId: null,
  datenschutz: 'Diese E-Mail ist nur für den genannten Empfänger bestimmt und kann vertrauliche Informationen enthalten. Falls Sie diese E-Mail irrtümlich erhalten haben, informieren Sie bitte umgehend den Absender und löschen Sie diese Nachricht. Der Verantwortliche gemäß DSGVO ist die oben genannte Organisation.',
  // Corporate Design
  fontFamily: 'Arial, Helvetica, sans-serif',
  dividerColor: '',          // empty = use accentColor
  dividerThickness: 'medium', // thin | medium | thick
  dividerStyle: 'solid',     // solid | dashed | dotted
  logoSize: 'medium',        // small | medium | large
  logoPosition: 'bottom',    // top (above name) | bottom (below contacts)
  photoShape: 'round',       // round | square
});

// ─── Sidebar nav ──────────────────────────────────────────────────────────────
const NAV = [
  { key: 'company',   label: 'Unternehmenseinstellungen', icon: Briefcase },
  { key: 'platforms', label: 'Bewertungsplattformen',     icon: ExternalLink },
  { key: 'design',    label: 'Design',                    icon: Palette },
  { key: 'privacy',   label: 'Datenschutz & AVV',         icon: Shield },
  // Ausgeblendet (Feedback 2026-07): Rollenverwaltung, Werbebanner, Signaturdesigner
  // — zu viel Testaufwand für MVP. Routen bleiben erreichbar, nur aus der Nav genommen.
  // { key: 'roles',     label: 'Rollenverwaltung',          icon: Users },
  // { key: 'banner',    label: 'Werbebanner',               icon: Palette },
  // { key: 'signature', label: 'Signaturdesigner',          icon: FileSignature },
  { key: 'billing',   label: 'Billing & Abonnement',      icon: CreditCard },
];

// ─── Shared: ImageUploadField ─────────────────────────────────────────────────
function ImageUploadField({ label, value, onChange, note }) {
  const inputRef = useRef();
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <label className="text-xs font-medium mb-1.5 block text-muted-foreground">{label}</label>
      <div className="flex gap-2 items-center">
        <div className="flex-1">
          <Input value={value} onChange={e => onChange(e.target.value)} placeholder="https://..." />
          {note && <p className="text-xs text-muted-foreground mt-1">{note}</p>}
        </div>
        <button type="button" onClick={() => inputRef.current?.click()}
          className="shrink-0 flex items-center gap-1.5 px-3 h-9 rounded-md border border-border text-xs hover:bg-muted transition-colors">
          <ImageIcon className="w-3.5 h-3.5" /> Upload
        </button>
        {value && (
          <img src={value} alt="" className="w-9 h-9 object-contain rounded border border-border shrink-0"
            onError={e => e.target.style.display = 'none'} />
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

// ─── Shared: FontPicker ───────────────────────────────────────────────────────
function FontPicker({ value, onChange }) {
  return (
    <div>
      <label className="text-xs font-medium mb-1.5 block text-muted-foreground">
        Schriftart <span className="text-muted-foreground/60 font-normal">(E-Mail-sicher)</span>
      </label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
        style={{ fontFamily: value }}>
        {EMAIL_FONTS.map(f => (
          <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground mt-1">
        ⚠ Web-Fonts (Google Fonts etc.) werden von Outlook/Gmail ignoriert. Nur System-Schriften funktionieren zuverlässig.
      </p>
    </div>
  );
}

// ─── Section: Unternehmenseinstellungen ───────────────────────────────────────
function CompanySection({ user }) {
  const [form, setForm] = useState({
    display_name: '', address_street: '',
    address_zip: '', address_city: '', phone: '', website: '',
  });
  const [legalLinks, setLegalLinks] = useState({ impressum: '', datenschutz: '', agb: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const p = user?.tenant?.public_profile || {};
    // Migration: früher lag PLZ+Stadt in einem Feld ("80331 München")
    const legacyCity = p.address_city || '';
    const legacyMatch = !p.address_zip && /^\s*(\d{4,5})\s+(.*)$/.exec(legacyCity);
    setForm({
      display_name: p.display_name || '',
      address_street: p.address_street || '',
      address_zip: p.address_zip || (legacyMatch ? legacyMatch[1] : ''),
      address_city: legacyMatch ? legacyMatch[2] : legacyCity,
      phone: p.phone || '', website: p.website || '',
    });
    const ll = user?.tenant?.legal_links || {};
    setLegalLinks({ impressum: ll.impressum || '', datenschutz: ll.datenschutz || '', agb: ll.agb || '' });
  }, [user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await hvApi.updateTenant({ ...form, legal_links: legalLinks }); toast.success('Gespeichert'); }
    catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profil Ihrer Hausverwaltung</CardTitle>
        <CardDescription>Diese Angaben erscheinen im Bewertungs-Funnel und in Ihrer Kommunikation.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
              Anzeigename
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground">
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px] text-xs">
                    So, wie Eigentümer &amp; Mieter Ihre Hausverwaltung im Bewertungs-Funnel sehen.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </label>
            <Input value={form.display_name} onChange={e => set('display_name', e.target.value)} placeholder="Muster HV GmbH" />
          </div>
          {[
            ['Straße & Hausnummer','address_street',  'Musterstraße 1',           'text'],
          ].map(([label, key, placeholder, type]) => (
            <div key={key}>
              <label className="text-sm font-medium mb-1.5 block">{label}</label>
              <Input value={form[key]} onChange={e => set(key, e.target.value)} placeholder={placeholder} type={type} />
            </div>
          ))}
          {/* PLZ und Stadt getrennt (Feedback Atarim-5) */}
          <div className="grid grid-cols-[100px_1fr] gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">PLZ</label>
              <Input value={form.address_zip} onChange={e => set('address_zip', e.target.value)} placeholder="80331" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Stadt</label>
              <Input value={form.address_city} onChange={e => set('address_city', e.target.value)} placeholder="München" />
            </div>
          </div>
          {[
            ['Telefon',            'phone',           '+49 89 123456',            'tel'],
            ['Website',            'website',         'https://www.beispiel.de', 'url'],
          ].map(([label, key, placeholder, type]) => (
            <div key={key}>
              <label className="text-sm font-medium mb-1.5 block">{label}</label>
              <Input value={form[key]} onChange={e => set(key, e.target.value)} placeholder={placeholder} type={type} />
            </div>
          ))}
        </div>
        {/* Logo & Farben leben jetzt in der eigenen Sektion „Design" */}
        {/* Bewertungsplattformen: eigene Sektion (siehe PlatformsSection) */}

        {/* Rechtliche Links für Funnel-Footer */}
        <div className="pt-2 border-t">
          <label className="text-sm font-medium block mb-0.5">Rechtliche Links (Funnel-Footer)</label>
          <p className="text-xs text-muted-foreground mb-3">
            Diese Links erscheinen im Footer des Bewertungs-Funnels Ihrer Bewerter.
          </p>
          <div className="space-y-3">
            {[
              { key: 'impressum',   label: 'Impressum',   placeholder: 'https://www.ihrehv.de/impressum' },
              { key: 'datenschutz', label: 'Datenschutz', placeholder: 'https://www.ihrehv.de/datenschutz' },
              { key: 'agb',         label: 'AGB',         placeholder: 'https://www.ihrehv.de/agb' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="text-xs font-medium mb-1 block text-muted-foreground">{label}</label>
                <Input
                  value={legalLinks[key]}
                  onChange={e => setLegalLinks(l => ({ ...l, [key]: e.target.value }))}
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>
        </div>

        {/* E-Mail-/Plan-Zeile entfernt (Atarim-9) — Plan steht im Billing-Bereich. */}
        <Button onClick={save} disabled={saving} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Speichern
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Section: Bewertungsplattformen ───────────────────────────────────────────
const PLATFORM_FIELDS = [
  { key: 'google',       label: 'Google',       placeholder: 'https://g.page/r/...',                  hint: 'Google-Unternehmensprofil → Bewertung anfordern → Link kopieren' },
  { key: 'provenexpert', label: 'ProvenExpert', placeholder: 'https://www.provenexpert.com/...',      hint: 'Ihr öffentliches ProvenExpert-Profil' },
  { key: 'trustpilot',   label: 'Trustpilot',   placeholder: 'https://www.trustpilot.com/review/...', hint: 'Ihre Trustpilot-Bewertungsseite' },
];

function PlatformsSection({ user }) {
  const [urls, setUrls] = useState({ google: '', provenexpert: '', trustpilot: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const pu = user?.tenant?.platform_urls || {};
    setUrls({ google: pu.google || '', provenexpert: pu.provenexpert || '', trustpilot: pu.trustpilot || '' });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try { await hvApi.updateTenant({ platform_urls: urls }); toast.success('Gespeichert'); }
    catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const filled = PLATFORM_FIELDS.filter(f => urls[f.key]).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Bewertungsplattformen</CardTitle>
        <CardDescription>
          Standard-Links, zu denen Eigentümer &amp; Mieter nach einer positiven Bewertung weitergeleitet werden.
          Einzelne Bewertungslinks können diese Vorgaben individuell überschreiben.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {filled === 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            Noch keine Plattform hinterlegt — im Funnel können Bewerter dann nirgends extern bewerten.
          </div>
        )}
        {PLATFORM_FIELDS.map(({ key, label, placeholder, hint }) => (
          <div key={key}>
            <label className="text-sm font-medium mb-1.5 block">{label}</label>
            <Input value={urls[key]} onChange={e => setUrls(u => ({ ...u, [key]: e.target.value }))} placeholder={placeholder} type="url" />
            <p className="text-xs text-muted-foreground mt-1">{hint}</p>
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Speichern
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Section: Design ──────────────────────────────────────────────────────────
const DESIGN_DEFAULTS = {
  brand_color: '#1a56db',
  secondary_color: '#4cb942',
  background_color: '#f5f7fa',
};

function DesignSection({ user, onProfileChange }) {
  const [colors, setColors] = useState(DESIGN_DEFAULTS);
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const p = user?.tenant?.public_profile || {};
    setColors({
      brand_color: p.brand_color || DESIGN_DEFAULTS.brand_color,
      secondary_color: p.secondary_color || DESIGN_DEFAULTS.secondary_color,
      background_color: p.background_color || DESIGN_DEFAULTS.background_color,
    });
    setLogoUrl(p.logo_url || '');
  }, [user]);

  const setColor = (k, v) => setColors(c => ({ ...c, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error('Logo darf maximal 512 KB groß sein.');
      return;
    }
    setUploading(true);
    try {
      const res = await hvApi.uploadLogo(file);
      setLogoUrl(res.data.logo_url);
      onProfileChange?.({ logo_url: res.data.logo_url });
      toast.success('Logo hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    try {
      await hvApi.updateTenant({ logo_url: '' });
      setLogoUrl('');
      onProfileChange?.({ logo_url: '' });
      toast.success('Logo entfernt');
    } catch { toast.error('Fehler beim Entfernen'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await hvApi.updateTenant(colors);
      onProfileChange?.(colors);
      toast.success('Gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const COLOR_FIELDS = [
    { key: 'brand_color',      label: 'Primärfarbe',      hint: 'Buttons, aktive Elemente und Akzente im Funnel.' },
    { key: 'secondary_color',  label: 'Sekundärfarbe',    hint: 'Ergänzende Akzente, z. B. Erfolgs-Hinweise.' },
    { key: 'background_color', label: 'Hintergrundfarbe', hint: 'Hintergrund des Bewertungs-Funnels.' },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Logo</CardTitle>
          <CardDescription>Wird im Bewertungs-Funnel Ihrer Eigentümer &amp; Mieter angezeigt.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-32 h-20 rounded-lg border border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl
                ? <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFile}
                className="hidden"
              />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                {logoUrl ? 'Logo ersetzen' : 'Logo hochladen'}
              </Button>
              {logoUrl && (
                <Button variant="ghost" size="sm" onClick={removeLogo} className="text-destructive">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Entfernen
                </Button>
              )}
              <p className="text-xs text-muted-foreground">PNG, JPEG oder WebP · max. 512 KB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Farben</CardTitle>
          <CardDescription>Passen Sie den Funnel an Ihr Corporate Design an.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {COLOR_FIELDS.map(({ key, label, hint }) => (
            <div key={key}>
              <label className="text-sm font-medium mb-1.5 block">{label}</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={colors[key]}
                  onChange={e => setColor(key, e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border border-border shrink-0"
                />
                <Input value={colors[key]} onChange={e => setColor(key, e.target.value)} className="flex-1" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{hint}</p>
            </div>
          ))}

          {/* Live-Vorschau */}
          <div className="pt-2 border-t">
            <p className="text-sm font-medium mb-2">Vorschau</p>
            <div className="rounded-xl border border-border p-4" style={{ background: colors.background_color }}>
              <div className="bg-white rounded-lg p-4 space-y-3">
                <div className="flex justify-center">
                  {logoUrl
                    ? <img src={logoUrl} alt="" className="h-10 object-contain" />
                    : <div className="h-10 flex items-center text-xs text-muted-foreground">Ihr Logo</div>}
                </div>
                <p className="text-center text-sm font-medium">Wie zufrieden sind Sie?</p>
                <div
                  className="rounded-lg py-2 text-center text-sm font-medium text-white"
                  style={{ background: colors.brand_color }}
                >
                  Bewertung abgeben
                </div>
                <div
                  className="rounded-lg py-1.5 text-center text-xs font-medium text-white"
                  style={{ background: colors.secondary_color }}
                >
                  Vielen Dank!
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
            <Button variant="ghost" onClick={() => setColors(DESIGN_DEFAULTS)}>
              Zurücksetzen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Betroffenenrechte: Export (Art. 15/20) und Konto-Löschung (Art. 17) ──────
// Auf Modul-Ebene definiert (SectionCard-Bug, siehe CLAUDE.md).
function AccountDataCard() {
  const [exporting, setExporting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await hvApi.exportAccount();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `immoverita-datenexport-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Datenexport heruntergeladen');
    } catch {
      toast.error('Export fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await hvApi.deleteAccount(confirmText);
      localStorage.removeItem('hv_auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen.');
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ihre Daten</CardTitle>
        <CardDescription>
          Alle gespeicherten Daten herunterladen oder das Konto endgültig löschen (Art. 15, 17, 20 DSGVO).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Button onClick={handleExport} variant="secondary" disabled={exporting} data-testid="account-export-button">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Daten exportieren (JSON)
          </Button>
          <p className="text-xs text-muted-foreground mt-1.5">
            Enthält Profil, Bewertungslinks, alle Bewertungen und Einstellungen.
          </p>
        </div>
        <div className="rounded-lg border border-destructive/30 p-4 space-y-3">
          <p className="text-sm font-medium">Konto löschen</p>
          <p className="text-xs text-muted-foreground">
            Löscht sofort und endgültig Ihr Konto, alle Bewertungslinks, alle eingegangenen Bewertungen
            samt E-Mail-Adressen der Bewerter und alle Einstellungen. Exportieren Sie Ihre Daten vorher.
            Aus Sicherungskopien verschwinden die Daten spätestens nach 14 Tagen.
          </p>
          <label htmlFor="account-delete-confirm" className="text-xs font-medium block">
            Zur Bestätigung <strong>LÖSCHEN</strong> eingeben
          </label>
          <Input
            id="account-delete-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="LÖSCHEN"
            className="max-w-xs"
            data-testid="account-delete-confirm"
          />
          <Button
            variant="destructive"
            disabled={confirmText.trim().toUpperCase() !== 'LÖSCHEN' || deleting}
            onClick={handleDelete}
            data-testid="account-delete-button"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Konto endgültig löschen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Section: Datenschutz & AVV ───────────────────────────────────────────────
function PrivacySection({ user }) {
  const profile = user?.tenant?.public_profile || {};
  const today = new Date().toLocaleDateString('de-DE');

  // Der AVV wird clientseitig aus den Stammdaten erzeugt — kein Backend nötig,
  // und der Kunde sieht sofort seine eigenen Daten eingesetzt.
  const buildAvv = () => `AUFTRAGSVERARBEITUNGSVERTRAG (AVV)
nach Art. 28 DSGVO

Stand: ${today}

────────────────────────────────────────────────
1. VERTRAGSPARTEIEN

Auftraggeber (Verantwortlicher):
${profile.legal_name || profile.display_name || '[Name der Hausverwaltung]'}
${profile.address_street || '[Straße]'}
${[profile.address_zip, profile.address_city].filter(Boolean).join(' ') || '[PLZ Ort]'}

Auftragnehmer (Auftragsverarbeiter):
Dwayne Advertising – Alexander Fischl (Immoverita)
Innere Wiener Straße 10
81667 München

────────────────────────────────────────────────
2. GEGENSTAND UND DAUER

Gegenstand ist die Erhebung und Verarbeitung von Bewertungsdaten
über die Plattform Immoverita. Die Laufzeit entspricht der Laufzeit
des Hauptvertrags.

────────────────────────────────────────────────
3. ART UND ZWECK DER VERARBEITUNG

- Erfassung von Bewertungen über personalisierte Bewertungslinks
- KI-gestützte Analyse von Bewertungstexten (Sachlichkeitsindex,
  Stimmung, Textvorschläge)
- Bereitstellung von Auswertungen im Dashboard

────────────────────────────────────────────────
4. ART DER PERSONENBEZOGENEN DATEN

- E-Mail-Adresse (freiwillig, zur Verifizierung/Bearbeitung)
- Freitext der Bewertung
- Technische Daten (Zeitstempel, Sitzungskennung)

Kategorien betroffener Personen: Eigentümer und Mieter der vom
Auftraggeber verwalteten Objekte.

────────────────────────────────────────────────
5. UNTERAUFTRAGSVERARBEITER

- Hosting: Hetzner Online GmbH, Gunzenhausen (Server in Deutschland)
- KI-Analyse: OpenAI, L.L.C., USA (Verarbeitung von Bewertungstexten;
  EU-US Data Privacy Framework + Standardvertragsklauseln, kein Training)
- E-Mail-Versand: Mailgun Technologies, Inc. (EU-Region), sobald der
  Mailversand aktiviert ist

Der Auftraggeber stimmt dem Einsatz dieser Unterauftragsverarbeiter zu.
Ein Wechsel wird rechtzeitig angekündigt.

────────────────────────────────────────────────
6. TECHNISCH-ORGANISATORISCHE MASSNAHMEN (Art. 32 DSGVO)

- Transportverschlüsselung (TLS) für alle Verbindungen
- Zugriff auf die Datenbank ausschließlich authentifiziert
- Serverzugang nur über SSH-Schlüssel, Brute-Force-Schutz aktiv
- Regelmäßige, rotierende Backups
- Mandantentrennung auf Datenebene
- Löschfristen nach Löschkonzept (siehe Datenschutzerklärung,
  Abschnitt „Speicherdauer"), Export und Löschung per Selbstbedienung

────────────────────────────────────────────────
7. RECHTE DES AUFTRAGGEBERS

Der Auftraggeber bleibt Verantwortlicher im Sinne der DSGVO.
Weisungen sind in Textform zu erteilen. Der Auftragnehmer
unterstützt bei Betroffenenanfragen, Löschung und Auskunft.

────────────────────────────────────────────────
HINWEIS: Dieses Dokument ist ein Entwurf zur Vorlage und ersetzt
keine Rechtsberatung. Bitte vor Unterzeichnung prüfen lassen.
`;

  const downloadAvv = () => {
    const blob = new Blob([buildAvv()], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AVV_Immoverita_${(profile.display_name || 'Hausverwaltung').replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('AVV heruntergeladen');
  };

  const FACTS = [
    { label: 'Serverstandort', value: 'Deutschland (Hetzner)' },
    { label: 'Verschlüsselung', value: 'TLS für alle Verbindungen' },
    { label: 'Backups', value: 'Täglich, rotierend über 14 Tage' },
    { label: 'Mandantentrennung', value: 'Daten je Hausverwaltung getrennt' },
  ];

  const AI_USES = [
    { what: 'Textvorschläge für positive Bewertungen', who: 'OpenAI (gpt-4o-mini)', data: 'Name der HV, gewählte Themen' },
    { what: 'Sachlichkeitsindex kritischer Texte',      who: 'OpenAI (gpt-4o-mini)', data: 'Freitext der Bewertung' },
    { what: 'Stimmungserkennung',                        who: 'OpenAI (gpt-4o-mini)', data: 'Freitext der Bewertung' },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            Auftragsverarbeitungsvertrag (AVV)
          </CardTitle>
          <CardDescription>
            Als Verantwortlicher benötigen Sie einen AVV nach Art. 28 DSGVO. Er wird mit Ihren
            hinterlegten Unternehmensdaten erzeugt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!profile.legal_name && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              Hinterlegen Sie zuerst Ihren <strong>rechtlichen Namen</strong> und die Anschrift unter
              „Unternehmenseinstellungen" — sonst bleiben im AVV Platzhalter stehen.
            </div>
          )}
          <Button onClick={downloadAvv} variant="secondary">
            <Download className="w-4 h-4 mr-2" />
            AVV herunterladen
          </Button>
          <p className="text-xs text-muted-foreground">
            Entwurf zur Vorlage — ersetzt keine Rechtsberatung.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Wie wir Ihre Daten verarbeiten</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            {FACTS.map(({ label, value }) => (
              <div key={label} className="py-2.5 flex justify-between gap-4 text-sm">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-medium text-right">{value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">KI-Einsatz — Transparenz</CardTitle>
          <CardDescription>Wo im Tool KI eingesetzt wird und welche Daten dabei verarbeitet werden.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {AI_USES.map(({ what, who, data }) => (
              <div key={what} className="rounded-lg border border-border p-3">
                <p className="text-sm font-medium">{what}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Anbieter: {who} · Übermittelte Daten: {data}
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Sternebewertungen werden von der KI <strong>nicht</strong> verändert. Bewerter entscheiden
            selbst, ob sie einen Textvorschlag übernehmen. Öffentliche Übersicht:{' '}
            <Link to="/ki-transparenz" className="underline">KI-Transparenz</Link>.
          </p>
        </CardContent>
      </Card>

      <AccountDataCard />
    </div>
  );
}

// ─── Section: Rollenverwaltung ────────────────────────────────────────────────
const ROLES = [
  { value: 'admin',   label: 'Administrator', desc: 'Voller Zugriff inkl. Billing und Einstellungen' },
  { value: 'manager', label: 'Manager',        desc: 'Links, Bewertungen, Statistiken verwalten' },
  { value: 'viewer',  label: 'Betrachter',     desc: 'Nur lesender Zugriff auf Bewertungen' },
];

function RolesSection({ user }) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]   = useState('manager');
  const [sending, setSending]         = useState(false);
  const members = [{ email: user?.email || '', role: 'admin', status: 'aktiv', you: true }];

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSending(true);
    await new Promise(r => setTimeout(r, 800));
    toast.success(`Einladung an ${inviteEmail} gesendet`);
    setInviteEmail('');
    setSending(false);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Team-Mitglieder</CardTitle>
          <CardDescription>Personen mit Zugriff auf Ihr Immoverita-Konto.</CardDescription>
        </CardHeader>
        <CardContent>
          {members.map((m, i) => (
            <div key={i} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-semibold text-primary">{(m.email?.[0] || '?').toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm font-medium">{m.email || '—'} {m.you && <span className="text-xs text-muted-foreground">(Sie)</span>}</p>
                  <p className="text-xs text-muted-foreground">{ROLES.find(r => r.value === m.role)?.label}</p>
                </div>
              </div>
              <Badge variant="outline" className="text-xs">{m.status}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mitarbeiter einladen</CardTitle>
          <CardDescription>Eingeladene Personen erhalten einen Magic-Link per E-Mail.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">E-Mail-Adresse</label>
            <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="kollege@beispiel.de" type="email" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Rolle</label>
            <div className="space-y-2">
              {ROLES.map(r => (
                <label key={r.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${inviteRole === r.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                  <input type="radio" name="role" value={r.value} checked={inviteRole === r.value} onChange={() => setInviteRole(r.value)} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{r.label}</p>
                    <p className="text-xs text-muted-foreground">{r.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <Button onClick={sendInvite} disabled={sending || !inviteEmail.trim()} className="w-full">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Einladung senden
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Berechtigungsübersicht</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium text-muted-foreground">Bereich</th>
                  {ROLES.map(r => <th key={r.value} className="text-center py-2 font-medium text-muted-foreground px-3">{r.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Bewertungen ansehen', admin: true,  manager: true,  viewer: true  },
                  { label: 'Links verwalten',     admin: true,  manager: true,  viewer: false },
                  { label: 'Einstellungen',       admin: true,  manager: false, viewer: false },
                  { label: 'Billing',             admin: true,  manager: false, viewer: false },
                  { label: 'Team verwalten',      admin: true,  manager: false, viewer: false },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="py-2">{row.label}</td>
                    {['admin','manager','viewer'].map(role => (
                      <td key={role} className="py-2 text-center px-3">
                        {row[role] ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <span className="text-muted-foreground/30">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Banner helpers ───────────────────────────────────────────────────────────
function buildBannerHTML(b, reviewUrl) {
  const link     = b.linkMode === 'auto' ? reviewUrl : (b.customLink || reviewUrl);
  const radius   = b.borderRadius ? `${b.borderRadius}px` : '12px';
  const align    = b.textAlign || 'left';
  const font     = b.fontFamily || 'Arial, Helvetica, sans-serif';
  const logoSz   = b.logoSize || 36;
  const img      = b.imageUrl ? `<img src="${b.imageUrl}" alt="" style="width:100%;display:block;" />` : '';
  const logoAbove = (b.logoUrl && b.logoPosition !== 'inline-left' && b.logoPosition !== 'inline-right')
    ? `<img src="${b.logoUrl}" alt="Logo" style="height:${logoSz}px;width:auto;display:block;margin-bottom:10px;" />`
    : '';
  const logoInlineLeft  = (b.logoUrl && b.logoPosition === 'inline-left')
    ? `<img src="${b.logoUrl}" alt="Logo" style="height:${logoSz}px;width:auto;vertical-align:middle;margin-right:12px;" />`
    : '';
  const logoInlineRight = (b.logoUrl && b.logoPosition === 'inline-right')
    ? `<img src="${b.logoUrl}" alt="Logo" style="height:${logoSz}px;width:auto;vertical-align:middle;float:right;margin-left:12px;" />`
    : '';
  const btnStyle = b.buttonStyle === 'outlined'
    ? `display:inline-block;border:2px solid ${b.textColor};color:${b.textColor};padding:6px 18px;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none;`
    : `display:inline-block;background:${b.textColor};color:${b.bgColor};padding:7px 18px;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none;`;

  return `<a href="${link}" target="_blank" rel="noopener" style="display:block;background:${b.bgColor};color:${b.textColor};font-family:${font};text-decoration:none;border-radius:${radius};overflow:hidden;max-width:600px;">${img}<div style="padding:20px 24px;text-align:${align};">${logoAbove}${logoInlineLeft}<p style="margin:0 0 4px;font-size:18px;font-weight:700;">${b.headline}</p><p style="margin:0 0 14px;font-size:13px;opacity:0.85;">${b.subtext}</p><span style="${btnStyle}">${b.cta} →</span>${logoInlineRight}</div></a>`;
}

function BannerPreview({ b, reviewUrl }) {
  const align      = b.textAlign || 'left';
  const font       = b.fontFamily || 'Arial, Helvetica, sans-serif';
  const radius     = b.borderRadius ? `${b.borderRadius}px` : '12px';
  const isOutlined = b.buttonStyle === 'outlined';
  const logoSz     = b.logoSize || 36;
  const logoAbove  = b.logoUrl && b.logoPosition !== 'inline-left' && b.logoPosition !== 'inline-right';
  const logoInL    = b.logoUrl && b.logoPosition === 'inline-left';
  const logoInR    = b.logoUrl && b.logoPosition === 'inline-right';

  return (
    <div style={{ background: b.bgColor, color: b.textColor, fontFamily: font, borderRadius: radius, overflow: 'hidden' }}>
      {b.imageUrl && (
        <img src={b.imageUrl} alt="" style={{ width: '100%', display: 'block', maxHeight: 120, objectFit: 'cover' }}
          onError={e => e.target.style.display = 'none'} />
      )}
      <div style={{ padding: '20px 24px', textAlign: align }}>
        {/* Logo above headline */}
        {logoAbove && b.logoUrl && (
          <img src={b.logoUrl} alt="Logo"
            style={{ height: logoSz, width: 'auto', display: 'block', marginBottom: 10 }}
            onError={e => e.target.style.display = 'none'} />
        )}
        {/* Logo inline-left + text */}
        <div style={{ display: 'flex', alignItems: 'center', gap: logoInL || logoInR ? 12 : 0 }}>
          {logoInL && b.logoUrl && (
            <img src={b.logoUrl} alt="Logo"
              style={{ height: logoSz, width: 'auto', flexShrink: 0 }}
              onError={e => e.target.style.display = 'none'} />
          )}
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>{b.headline || 'Überschrift'}</p>
            <p style={{ margin: '0 0 14px', fontSize: 13, opacity: 0.85 }}>{b.subtext || 'Untertitel'}</p>
            <span style={{
              display: 'inline-block', padding: '7px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              ...(isOutlined
                ? { border: `2px solid ${b.textColor}`, color: b.textColor }
                : { background: b.textColor, color: b.bgColor })
            }}>
              {b.cta || 'Button'} →
            </span>
          </div>
          {logoInR && b.logoUrl && (
            <img src={b.logoUrl} alt="Logo"
              style={{ height: logoSz, width: 'auto', flexShrink: 0 }}
              onError={e => e.target.style.display = 'none'} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Section: Werbebanner ─────────────────────────────────────────────────────
function BannerEdit({ banner, onSave, onCancel, reviewUrl }) {
  const [b, setB] = useState({ ...banner });
  const set = (k, v) => setB(prev => ({ ...prev, [k]: v }));
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'mobile'

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="p-1 rounded hover:bg-muted transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <CardTitle className="text-base">{b.name}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Interner Name</label>
            <Input value={b.name} onChange={e => set('name', e.target.value)} placeholder="z.B. Newsletter Q1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">Überschrift</label>
              <Input value={b.headline} onChange={e => set('headline', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">Untertitel</label>
              <Input value={b.subtext} onChange={e => set('subtext', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">Button-Text</label>
              <Input value={b.cta} onChange={e => set('cta', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">Link</label>
              <select value={b.linkMode} onChange={e => set('linkMode', e.target.value)}
                className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm">
                <option value="auto">Automatisch (Bewertungslink)</option>
                <option value="custom">Eigene URL</option>
              </select>
              {b.linkMode === 'custom' && (
                <Input className="mt-2" value={b.customLink} onChange={e => set('customLink', e.target.value)} placeholder="https://..." />
              )}
            </div>
          </div>
          <ImageUploadField label="Hero-Bild (optional, volle Breite)" value={b.imageUrl} onChange={v => set('imageUrl', v)}
            note="Empfohlen: 600×200px. Für E-Mails gehostete URL verwenden." />
          <ImageUploadField label="Logo (optional)" value={b.logoUrl} onChange={v => set('logoUrl', v)}
            note="Empfohlen: transparenter Hintergrund (PNG). Für E-Mails gehostete URL verwenden." />
          {b.logoUrl && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium mb-1.5 block text-muted-foreground">
                  Logo-Größe: <strong>{b.logoSize || 36}px</strong>
                </label>
                <input
                  type="range" min={16} max={120} step={4}
                  value={b.logoSize || 36}
                  onChange={e => set('logoSize', Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                  <span>16px</span><span>60px</span><span>120px</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Logo-Position</label>
                <div className="flex gap-1">
                  {[
                    { l: 'Über Text', v: 'above' },
                    { l: '← Links', v: 'inline-left' },
                    { l: 'Rechts →', v: 'inline-right' },
                  ].map(o => (
                    <button key={o.v} type="button" onClick={() => set('logoPosition', o.v)}
                      className={`flex-1 py-1.5 text-xs rounded border transition-colors ${(b.logoPosition || 'above') === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Corporate Design */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Sliders className="w-4 h-4" /> Corporate Design</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Hintergrundfarbe</label>
              <div className="flex gap-2">
                <input type="color" value={b.bgColor} onChange={e => set('bgColor', e.target.value)} className="w-10 h-9 rounded border cursor-pointer" />
                <Input value={b.bgColor} onChange={e => set('bgColor', e.target.value)} className="flex-1" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Textfarbe</label>
              <div className="flex gap-2">
                <input type="color" value={b.textColor} onChange={e => set('textColor', e.target.value)} className="w-10 h-9 rounded border cursor-pointer" />
                <Input value={b.textColor} onChange={e => set('textColor', e.target.value)} className="flex-1" />
              </div>
            </div>
          </div>
          <FontPicker value={b.fontFamily} onChange={v => set('fontFamily', v)} />
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Textausrichtung</label>
              <div className="flex gap-1">
                {['left','center','right'].map(a => (
                  <button key={a} onClick={() => set('textAlign', a)}
                    className={`flex-1 py-1.5 text-xs rounded border transition-colors ${b.textAlign === a ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                    {a === 'left' ? '⬅' : a === 'center' ? '↔' : '➡'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Ecken</label>
              <div className="flex gap-1">
                {[{ label: 'Eckig', val: '0' }, { label: 'Rund', val: '12' }, { label: 'Oval', val: '24' }].map(o => (
                  <button key={o.val} onClick={() => set('borderRadius', o.val)}
                    className={`flex-1 py-1.5 text-xs rounded border transition-colors ${b.borderRadius === o.val ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Button-Stil</label>
              <div className="flex gap-1">
                {[{ label: 'Gefüllt', val: 'filled' }, { label: 'Umriss', val: 'outlined' }].map(o => (
                  <button key={o.val} onClick={() => set('buttonStyle', o.val)}
                    className={`flex-1 py-1.5 text-xs rounded border transition-colors ${b.buttonStyle === o.val ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Vorschau</CardTitle>
            {/* Device toggle */}
            <div className="flex rounded-lg border border-border overflow-hidden text-xs">
              {[
                { v: 'desktop', l: '🖥 Desktop' },
                { v: 'mobile',  l: '📱 Mobil' },
              ].map(d => (
                <button key={d.v} type="button" onClick={() => setPreviewDevice(d.v)}
                  className={`px-3 py-1.5 transition-colors ${previewDevice === d.v ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}>
                  {d.l}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Mobile: phone frame */}
          {previewDevice === 'mobile' ? (
            <div className="flex justify-center">
              <div className="relative border-[6px] border-slate-800 rounded-[2rem] overflow-hidden shadow-xl bg-white"
                   style={{ width: 320 }}>
                {/* Phone notch */}
                <div className="h-6 bg-slate-800 flex items-center justify-center">
                  <div className="w-16 h-1.5 rounded-full bg-slate-600" />
                </div>
                <div className="overflow-auto" style={{ maxHeight: 480 }}>
                  <div style={{ maxWidth: 320, margin: '0 auto' }}>
                    <BannerPreview b={b} reviewUrl={reviewUrl} />
                  </div>
                </div>
                {/* Home bar */}
                <div className="h-5 bg-white flex items-center justify-center border-t border-slate-100">
                  <div className="w-24 h-1 rounded-full bg-slate-300" />
                </div>
              </div>
            </div>
          ) : (
            <BannerPreview b={b} reviewUrl={reviewUrl} />
          )}
          <div className="flex gap-2">
            <Button onClick={() => onSave(b)} className="flex-1"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(buildBannerHTML(b, reviewUrl)); toast.success('HTML kopiert!'); }}>
              <Copy className="w-4 h-4 mr-2" /> HTML
            </Button>
            <Button variant="outline" asChild>
              <a href={b.linkMode === 'auto' ? reviewUrl : b.customLink || reviewUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BannerSection({ user }) {
  const reviewUrl = `${window.location.origin}/r/${user?.tenant?.slug}/hauptseite`;
  const [banners, setBanners] = useState(() => loadLS(LS_BANNERS, []));
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { saveLS(LS_BANNERS, banners); }, [banners]);

  const handleSave = (b) => {
    setBanners(prev => {
      const exists = prev.find(x => x.id === b.id);
      return exists ? prev.map(x => x.id === b.id ? b : x) : [...prev, b];
    });
    setEditingId(null);
    toast.success('Banner gespeichert');
  };

  const handleDelete = (id) => { setBanners(prev => prev.filter(x => x.id !== id)); toast.success('Banner gelöscht'); };
  const handleDuplicate = (b) => {
    const copy = { ...b, id: uid(), name: `${b.name} (Kopie)` };
    setBanners(prev => [...prev, copy]);
    toast.success('Banner dupliziert');
  };

  if (editingId !== null) {
    const banner = editingId === 'new' ? defaultBanner() : (banners.find(b => b.id === editingId) || defaultBanner());
    return <BannerEdit banner={banner} onSave={handleSave} onCancel={() => setEditingId(null)} reviewUrl={reviewUrl} />;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Werbebanner</CardTitle>
            <CardDescription>Klickbare HTML-Banner für Newsletter, Website und E-Mail-Signaturen.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setEditingId('new')}><Plus className="w-4 h-4 mr-1.5" /> Neuer Banner</Button>
        </CardHeader>
        <CardContent>
          {banners.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Palette className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Noch keine Banner erstellt.</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setEditingId('new')}>
                <Plus className="w-4 h-4 mr-1.5" /> Ersten Banner erstellen
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {banners.map(b => (
                <div key={b.id} className="border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
                    <p className="text-sm font-medium">{b.name}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(b)} title="Duplizieren"><Copy className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(b.id)}><Edit2 className="w-3.5 h-3.5 mr-1" /> Bearbeiten</Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDelete(b.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div className="p-3"><BannerPreview b={b} reviewUrl={reviewUrl} /></div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Signature helpers ────────────────────────────────────────────────────────
function buildSignatureHTML(s, reviewUrl, banners) {
  const banner   = banners.find(b => b.id === s.bannerId);
  const bLink    = banner ? (banner.linkMode === 'auto' ? reviewUrl : (banner.customLink || reviewUrl)) : reviewUrl;
  const font     = s.fontFamily || 'Arial, Helvetica, sans-serif';
  const divColor = s.dividerColor || s.accentColor;
  const divPx    = DIVIDER_PX[s.dividerThickness] || 2;
  const divStyle = s.dividerStyle || 'solid';
  const logoH    = LOGO_SIZES[s.logoSize] || 36;
  const photoR   = s.photoShape === 'square' ? '6px' : '50%';

  const logoHTML = s.logoUrl ? `<tr><td colspan="2" style="padding-top:8px;"><img src="${s.logoUrl}" alt="Logo" style="height:${logoH}px;width:auto;" /></td></tr>` : '';

  const contactRows = [
    s.phone       && `<tr><td style="padding:1px 0;color:${s.accentColor};">📞</td><td style="padding:1px 0 1px 6px;">${s.phone}</td></tr>`,
    s.email       && `<tr><td style="padding:1px 0;color:${s.accentColor};">✉</td><td style="padding:1px 0 1px 6px;"><a href="mailto:${s.email}" style="color:${s.accentColor};text-decoration:none;">${s.email}</a></td></tr>`,
    s.website     && `<tr><td style="padding:1px 0;color:${s.accentColor};">🌐</td><td style="padding:1px 0 1px 6px;"><a href="${s.website.startsWith('http') ? s.website : 'https://'+s.website}" style="color:${s.accentColor};text-decoration:none;">${s.website}</a></td></tr>`,
    s.address     && `<tr><td style="padding:1px 0;color:${s.accentColor};">📍</td><td style="padding:1px 0 1px 6px;">${s.address}</td></tr>`,
    s.calendarLink && `<tr><td style="padding:1px 0;color:${s.accentColor};">🗓</td><td style="padding:1px 0 1px 6px;"><a href="${s.calendarLink}" style="color:${s.accentColor};text-decoration:none;">${s.calendarLinkText || 'Termin vereinbaren'}</a></td></tr>`,
  ].filter(Boolean).join('\n');

  const ratingBtn = s.showRatingButton ? `<tr><td colspan="2" style="padding-top:10px;"><a href="${reviewUrl}" target="_blank" style="display:inline-block;background:${s.accentColor};color:#ffffff;text-decoration:none;padding:7px 16px;border-radius:7px;font-size:12px;font-weight:700;font-family:${font};">⭐ ${s.ratingButtonText || 'Jetzt bewerten'}</a></td></tr>` : '';

  const nameBlock = `
    <p style="margin:0;font-weight:700;font-size:15px;color:#111827;font-family:${font};">${s.personName || ''}</p>
    ${(s.personTitle || s.personTagline) ? `<p style="margin:0;font-size:12px;color:#6b7280;font-family:${font};">${[s.personTitle, s.personTagline].filter(Boolean).join(' · ')}</p>` : ''}
    ${s.companyName ? `<p style="margin:2px 0 0;font-size:12px;font-weight:600;color:#374151;font-family:${font};">${s.companyName}</p>` : ''}
    ${s.services    ? `<p style="margin:0;font-size:11px;color:#9ca3af;font-family:${font};">${s.services}</p>` : ''}
  `;

  const topLogoHTML = (s.logoUrl && s.logoPosition === 'top')
    ? `<tr><td colspan="2" style="padding-bottom:8px;"><img src="${s.logoUrl}" alt="Logo" style="height:${logoH}px;width:auto;" /></td></tr>` : '';
  const botLogoHTML = (s.logoUrl && s.logoPosition !== 'top') ? logoHTML : '';

  const bannerBlock = banner ? `
    <tr><td style="padding-top:16px;">
      ${buildBannerHTML(banner, bLink).replace('600px', '480px')}
    </td></tr>` : '';

  const datenschutzBlock = s.datenschutz ? `
    <tr><td style="padding-top:14px;border-top:1px solid #e5e7eb;">
      <p style="margin:0;font-size:9px;color:#9ca3af;line-height:1.4;font-family:${font};">${s.datenschutz}</p>
    </td></tr>` : '';

  const photoBlock = s.photoUrl ? `<td style="padding-right:14px;vertical-align:top;"><img src="${s.photoUrl}" alt="${s.personName}" style="width:56px;height:56px;border-radius:${photoR};object-fit:cover;" /></td>` : '';

  return `<table cellpadding="0" cellspacing="0" style="font-family:${font};font-size:13px;color:#374151;line-height:1.5;max-width:520px;">
  <tr>
    ${photoBlock}
    <td style="vertical-align:top;">
      <table cellpadding="0" cellspacing="0">
        ${topLogoHTML}
        <tr>
          <td style="padding-bottom:6px;border-bottom:${divPx}px ${divStyle} ${divColor};">
            ${nameBlock}
          </td>
        </tr>
        <tr><td style="padding-top:8px;">
          <table cellpadding="0" cellspacing="0" style="font-size:12px;font-family:${font};">
            ${contactRows}
            ${ratingBtn}
            ${botLogoHTML}
          </table>
        </td></tr>
        ${bannerBlock}
        ${datenschutzBlock}
      </table>
    </td>
  </tr>
</table>`;
}

function SignaturePreview({ s, reviewUrl, banners }) {
  const banner   = banners.find(b => b.id === s.bannerId);
  const bLink    = banner ? (banner.linkMode === 'auto' ? reviewUrl : (banner.customLink || reviewUrl)) : reviewUrl;
  const font     = s.fontFamily || 'Arial, Helvetica, sans-serif';
  const divColor = s.dividerColor || s.accentColor;
  const divPx    = DIVIDER_PX[s.dividerThickness] || 2;
  const divStyle = s.dividerStyle || 'solid';
  const logoH    = LOGO_SIZES[s.logoSize] || 36;
  const photoR   = s.photoShape === 'square' ? 4 : '50%';

  return (
    <div style={{ fontFamily: font, fontSize: 13, color: '#374151', lineHeight: 1.5, maxWidth: 520 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {s.photoUrl && (
          <img src={s.photoUrl} alt={s.personName}
            style={{ width: 56, height: 56, borderRadius: photoR, objectFit: 'cover', marginRight: 14, flexShrink: 0 }}
            onError={e => e.target.style.display = 'none'} />
        )}
        <div style={{ flex: 1 }}>
          {/* Top logo */}
          {s.logoUrl && s.logoPosition === 'top' && (
            <div style={{ marginBottom: 8 }}>
              <img src={s.logoUrl} alt="Logo" style={{ height: logoH, width: 'auto' }} onError={e => e.target.style.display = 'none'} />
            </div>
          )}
          {/* Name block */}
          <div style={{ paddingBottom: 6, borderBottom: `${divPx}px ${divStyle} ${divColor}`, marginBottom: 8 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#111827', fontFamily: font }}>{s.personName || <em style={{ color: '#9ca3af' }}>Name</em>}</p>
            {(s.personTitle || s.personTagline) && (
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280', fontFamily: font }}>{[s.personTitle, s.personTagline].filter(Boolean).join(' · ')}</p>
            )}
            {s.companyName && <p style={{ margin: '2px 0 0', fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: font }}>{s.companyName}</p>}
            {s.services    && <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontFamily: font }}>{s.services}</p>}
          </div>
          {/* Contact */}
          <table style={{ fontSize: 12, fontFamily: font }}>
            <tbody>
              {s.phone       && <tr><td style={{ color: s.accentColor }}>📞</td><td style={{ paddingLeft: 6 }}>{s.phone}</td></tr>}
              {s.email       && <tr><td style={{ color: s.accentColor }}>✉</td><td style={{ paddingLeft: 6 }}><a href={`mailto:${s.email}`} style={{ color: s.accentColor, textDecoration: 'none' }}>{s.email}</a></td></tr>}
              {s.website     && <tr><td style={{ color: s.accentColor }}>🌐</td><td style={{ paddingLeft: 6 }}><a href={s.website.startsWith('http') ? s.website : 'https://'+s.website} style={{ color: s.accentColor, textDecoration: 'none' }}>{s.website}</a></td></tr>}
              {s.address     && <tr><td style={{ color: s.accentColor }}>📍</td><td style={{ paddingLeft: 6 }}>{s.address}</td></tr>}
              {s.calendarLink && <tr><td style={{ color: s.accentColor }}>🗓</td><td style={{ paddingLeft: 6 }}><a href={s.calendarLink} style={{ color: s.accentColor, textDecoration: 'none' }}>{s.calendarLinkText || 'Termin vereinbaren'}</a></td></tr>}
              {s.showRatingButton && (
                <tr><td colSpan={2} style={{ paddingTop: 10 }}>
                  <span style={{ display: 'inline-block', background: s.accentColor, color: '#fff', padding: '7px 16px', borderRadius: 7, fontSize: 12, fontWeight: 700, fontFamily: font }}>
                    ⭐ {s.ratingButtonText || 'Jetzt bewerten'}
                  </span>
                </td></tr>
              )}
              {/* Bottom logo */}
              {s.logoUrl && s.logoPosition !== 'top' && (
                <tr><td colSpan={2} style={{ paddingTop: 8 }}>
                  <img src={s.logoUrl} alt="Logo" style={{ height: logoH, width: 'auto' }} onError={e => e.target.style.display = 'none'} />
                </td></tr>
              )}
            </tbody>
          </table>
          {/* Banner */}
          {banner && (
            <div style={{ marginTop: 16 }}>
              <BannerPreview b={banner} reviewUrl={bLink} />
            </div>
          )}
          {/* Datenschutz */}
          {s.datenschutz && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
              <p style={{ margin: 0, fontSize: 9, color: '#9ca3af', lineHeight: 1.4, fontFamily: font }}>{s.datenschutz}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared: SectionCard (must be module-level to avoid re-mount on parent re-render) ──
function SectionCard({ icon: Icon, title, children }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

// ─── Section: Signaturdesigner ────────────────────────────────────────────────
function SignatureEdit({ sig, onSave, onCancel, user, banners }) {
  const [s, setS] = useState({ ...sig });
  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const [copied, setCopied] = useState(false);
  const reviewUrl = `${window.location.origin}/r/${user?.tenant?.slug}/hauptseite`;

  const copyHTML = () => {
    navigator.clipboard.writeText(buildSignatureHTML(s, reviewUrl, banners));
    setCopied(true);
    toast.success('HTML kopiert — in E-Mail-Client einfügen');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onCancel} className="p-1.5 rounded hover:bg-muted transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <h2 className="text-base font-semibold">Signatur bearbeiten</h2>
      </div>

      {/* Name */}
      <Card>
        <CardContent className="pt-4">
          <label className="text-sm font-medium mb-1.5 block">Interner Name</label>
          <Input value={s.name} onChange={e => set('name', e.target.value)} placeholder="z.B. Alexander Fischl" />
        </CardContent>
      </Card>

      {/* Person */}
      <SectionCard icon={User} title="Person">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Name</label><Input value={s.personName} onChange={e => set('personName', e.target.value)} placeholder="Alexander Fischl" /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Berufsbezeichnung</label><Input value={s.personTitle} onChange={e => set('personTitle', e.target.value)} placeholder="Inhaber" /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Tagline / Hashtag</label><Input value={s.personTagline} onChange={e => set('personTagline', e.target.value)} placeholder="#gerneperDU" /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Firmenname</label><Input value={s.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Dwayne Advertising" /></div>
        </div>
        <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Leistungen</label><Input value={s.services} onChange={e => set('services', e.target.value)} placeholder="Webdesign · SEO · SEA" /></div>
      </SectionCard>

      {/* Contact */}
      <SectionCard icon={Phone} title="Kontakt">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Telefon</label><Input value={s.phone} onChange={e => set('phone', e.target.value)} placeholder="+49 (0) 89 - 99 2 999 88" /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">E-Mail</label><Input value={s.email} onChange={e => set('email', e.target.value)} type="email" /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Website</label><Input value={s.website} onChange={e => set('website', e.target.value)} placeholder="dwayne-advertising.de" /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Adresse</label><Input value={s.address} onChange={e => set('address', e.target.value)} placeholder="Musterstr. 1, 80331 München" /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Kalender-Link</label><Input value={s.calendarLink} onChange={e => set('calendarLink', e.target.value)} placeholder="https://calendly.com/..." /></div>
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Kalender-Linktext</label><Input value={s.calendarLinkText} onChange={e => set('calendarLinkText', e.target.value)} placeholder="Termin vereinbaren" /></div>
        </div>
      </SectionCard>

      {/* Corporate Design */}
      <SectionCard icon={Sliders} title="Corporate Design">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Akzentfarbe</label>
            <div className="flex gap-2">
              <input type="color" value={s.accentColor} onChange={e => set('accentColor', e.target.value)} className="w-10 h-9 rounded border cursor-pointer" />
              <Input value={s.accentColor} onChange={e => set('accentColor', e.target.value)} className="flex-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block text-muted-foreground">
              Trennlinienfarbe <span className="font-normal opacity-60">(leer = Akzentfarbe)</span>
            </label>
            <div className="flex gap-2">
              <input type="color" value={s.dividerColor || s.accentColor} onChange={e => set('dividerColor', e.target.value)} className="w-10 h-9 rounded border cursor-pointer" />
              <Input value={s.dividerColor} onChange={e => set('dividerColor', e.target.value)} placeholder="Wie Akzentfarbe" className="flex-1" />
            </div>
          </div>
        </div>
        <FontPicker value={s.fontFamily} onChange={v => set('fontFamily', v)} />
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Trennlinienstärke</label>
            <div className="flex gap-1">
              {[{l:'Dünn',v:'thin'},{l:'Normal',v:'medium'},{l:'Dick',v:'thick'}].map(o => (
                <button key={o.v} onClick={() => set('dividerThickness', o.v)}
                  className={`flex-1 py-1.5 text-xs rounded border transition-colors ${s.dividerThickness === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Linienstil</label>
            <div className="flex gap-1">
              {[{l:'—',v:'solid'},{l:'- -',v:'dashed'},{l:'···',v:'dotted'}].map(o => (
                <button key={o.v} onClick={() => set('dividerStyle', o.v)}
                  className={`flex-1 py-1.5 text-xs rounded border transition-colors ${s.dividerStyle === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Foto-Form</label>
            <div className="flex gap-1">
              {[{l:'Rund',v:'round'},{l:'Quadrat',v:'square'}].map(o => (
                <button key={o.v} onClick={() => set('photoShape', o.v)}
                  className={`flex-1 py-1.5 text-xs rounded border transition-colors ${s.photoShape === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <ImageUploadField label="Logo" value={s.logoUrl} onChange={v => set('logoUrl', v)}
          note="Für E-Mails bitte gehostete URL verwenden — kein Base64." />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Logo-Größe</label>
            <div className="flex gap-1">
              {[{l:'Klein',v:'small'},{l:'Mittel',v:'medium'},{l:'Groß',v:'large'}].map(o => (
                <button key={o.v} onClick={() => set('logoSize', o.v)}
                  className={`flex-1 py-1.5 text-xs rounded border transition-colors ${s.logoSize === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Logo-Position</label>
            <div className="flex gap-1">
              {[{l:'Oben',v:'top'},{l:'Unten',v:'bottom'}].map(o => (
                <button key={o.v} onClick={() => set('logoPosition', o.v)}
                  className={`flex-1 py-1.5 text-xs rounded border transition-colors ${s.logoPosition === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <ImageUploadField label="Foto der Person" value={s.photoUrl} onChange={v => set('photoUrl', v)}
          note="Empfohlen: quadratisch, mind. 120×120px." />
      </SectionCard>

      {/* Rating Button */}
      <SectionCard icon={Star} title="Bewertungsbutton">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" checked={s.showRatingButton} onChange={e => set('showRatingButton', e.target.checked)} className="w-4 h-4" />
          <span className="text-sm">Bewertungsbutton anzeigen</span>
        </label>
        {s.showRatingButton && (
          <div><label className="text-xs font-medium mb-1 block text-muted-foreground">Button-Text</label>
            <Input value={s.ratingButtonText} onChange={e => set('ratingButtonText', e.target.value)} placeholder="Jetzt bewerten" /></div>
        )}
      </SectionCard>

      {/* Banner */}
      <SectionCard icon={Palette} title="Werbebanner (optional)">
        {banners.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Noch kein Banner erstellt.{' '}
            <Link to="/app/settings/banner" className="text-primary underline">Banner erstellen →</Link>
          </p>
        ) : (
          <div className="space-y-2">
            <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${s.bannerId === null ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
              <input type="radio" checked={s.bannerId === null} onChange={() => set('bannerId', null)} />
              <span className="text-sm text-muted-foreground">Kein Banner</span>
            </label>
            {banners.map(b => (
              <label key={b.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${s.bannerId === b.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                <input type="radio" checked={s.bannerId === b.id} onChange={() => set('bannerId', b.id)} />
                <div className="w-6 h-6 rounded shrink-0" style={{ background: b.bgColor }} />
                <span className="text-sm">{b.name}</span>
              </label>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Datenschutz */}
      <SectionCard icon={Shield} title="Datenschutztext">
        <textarea value={s.datenschutz} onChange={e => set('datenschutz', e.target.value)} rows={3}
          className="w-full text-xs rounded-md border border-border bg-background px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Diese E-Mail ist nur für den genannten Empfänger bestimmt..." />
        <p className="text-xs text-muted-foreground">Wird klein am Ende der Signatur angezeigt.</p>
      </SectionCard>

      {/* Preview */}
      <Card>
        <CardHeader><CardTitle className="text-base">Vorschau</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="p-5 bg-white rounded-lg border">
            <SignaturePreview s={s} reviewUrl={reviewUrl} banners={banners} />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => onSave(s)} variant="outline" className="flex-1"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
            <Button onClick={copyHTML} className="flex-1">
              {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Kopiert!' : 'HTML kopieren'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Outlook: Datei → Optionen → E-Mail → Signaturen &nbsp;|&nbsp; Gmail: Einstellungen → Allgemein → Signatur
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function SignatureSection({ user }) {
  const banners   = loadLS(LS_BANNERS, []);
  const reviewUrl = `${window.location.origin}/r/${user?.tenant?.slug}/hauptseite`;
  const [signatures, setSignatures] = useState(() => loadLS(LS_SIGNATURES, []));
  const [editingId, setEditingId]   = useState(null);

  useEffect(() => { saveLS(LS_SIGNATURES, signatures); }, [signatures]);

  const handleSave = (s) => {
    setSignatures(prev => {
      const exists = prev.find(x => x.id === s.id);
      return exists ? prev.map(x => x.id === s.id ? s : x) : [...prev, s];
    });
    setEditingId(null);
    toast.success('Signatur gespeichert');
  };

  const handleDelete = (id) => { setSignatures(prev => prev.filter(x => x.id !== id)); toast.success('Signatur gelöscht'); };
  const handleDuplicate = (s) => {
    const copy = { ...s, id: uid(), name: `${s.name} (Kopie)` };
    setSignatures(prev => [...prev, copy]);
    toast.success('Signatur dupliziert');
  };

  if (editingId !== null) {
    const sig = editingId === 'new' ? defaultSignature(user) : (signatures.find(s => s.id === editingId) || defaultSignature(user));
    return <SignatureEdit sig={sig} onSave={handleSave} onCancel={() => setEditingId(null)} user={user} banners={banners} />;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">E-Mail-Signaturen</CardTitle>
            <CardDescription>Individuelle Signaturen für jeden Mitarbeiter — mit Bewertungsbutton, Banner und Datenschutztext.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setEditingId('new')}><Plus className="w-4 h-4 mr-1.5" /> Neue Signatur</Button>
        </CardHeader>
        <CardContent>
          {signatures.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <FileSignature className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Noch keine Signaturen erstellt.</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setEditingId('new')}>
                <Plus className="w-4 h-4 mr-1.5" /> Erste Signatur erstellen
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {signatures.map(s => (
                <div key={s.id} className="border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
                    <div>
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.personName}{s.personTitle ? ` · ${s.personTitle}` : ''}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(buildSignatureHTML(s, reviewUrl, banners)); toast.success('HTML kopiert!'); }}>
                        <Copy className="w-3.5 h-3.5 mr-1" /> HTML
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(s)} title="Duplizieren"><Copy className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(s.id)}><Edit2 className="w-3.5 h-3.5 mr-1" /> Bearbeiten</Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDelete(s.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div className="p-4 bg-white">
                    <SignaturePreview s={s} reviewUrl={reviewUrl} banners={banners} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Section: Billing ─────────────────────────────────────────────────────────
function CouponRedeemCard() {
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const res = await hvApi.redeemCoupon(code.trim());
      toast.success(res.data.message || 'Gutschein eingelöst');
      setCode('');
      // Plan hat sich geändert — neu laden, damit alle Limits greifen
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gutschein konnte nicht eingelöst werden');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Ticket className="w-4 h-4 text-primary" />
          Gutschein einlösen
        </CardTitle>
        <CardDescription>Haben Sie einen Aktionscode erhalten? Hier freischalten.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <Input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && redeem()}
            placeholder="z. B. PILOT2026"
            className="font-mono"
          />
          <Button onClick={redeem} disabled={redeeming || !code.trim()}>
            {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Einlösen'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Verwaltung des Abos läuft bewusst im Stripe-Kundenportal (Vorbild ChatGPT):
// Zahlungsmittel, Rechnungen, Kündigung und Planwechsel bleiben bei Stripe,
// damit wir nichts doppelt pflegen.
function StripePortalCard() {
  const [status, setStatus] = useState(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    hvApi.getBillingStatus()
      .then(res => setStatus(res.data))
      .catch(() => setStatus(null));
  }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const res = await hvApi.createPortalSession();
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kundenportal nicht verfügbar');
      setOpening(false);
    }
  };

  if (!status) return null;

  if (!status.stripe_configured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Abo verwalten</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted/50 border border-border p-3 text-sm text-muted-foreground">
            Die Zahlungsabwicklung ist noch nicht aktiv. Für Änderungen an Ihrem Plan
            melden Sie sich bitte unter{' '}
            <a href="mailto:af@dwayne-advertising.de" className="underline hover:text-foreground">af@dwayne-advertising.de</a>.
          </div>
        </CardContent>
      </Card>
    );
  }

  const sub = status.subscription;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Abo verwalten</CardTitle>
        <CardDescription>
          Zahlungsmittel, Rechnungen und Kündigung verwalten Sie sicher bei Stripe.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {status.source?.startsWith('coupon:') && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
            Ihr Plan wurde über den Gutschein <strong>{status.source.split(':')[1]}</strong> freigeschaltet
            {status.until ? ` — gültig bis ${new Date(status.until).toLocaleDateString('de-DE')}.` : ' — unbefristet.'}
          </div>
        )}
        {sub && (
          <div className="text-sm text-muted-foreground">
            Status: <span className="font-medium text-foreground">{sub.status}</span>
            {sub.cancel_at_period_end && ' · läuft zum Periodenende aus'}
          </div>
        )}
        <Button onClick={openPortal} disabled={opening} className="w-full">
          {opening ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
          Zum Stripe-Kundenportal
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

function LegalEntityCard({ user }) {
  const [legalName, setLegalName] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLegalName(user?.tenant?.public_profile?.legal_name || ''); }, [user]);
  const save = async () => {
    setSaving(true);
    try { await hvApi.updateTenant({ legal_name: legalName }); toast.success('Gespeichert'); }
    catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Rechnungsempfänger</CardTitle>
        <CardDescription>Rechtlicher Name, wie er auf Rechnungen und im AVV erscheint.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Rechtlicher Name</label>
          <Input value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Muster HV GmbH & Co. KG" />
          <p className="text-xs text-muted-foreground mt-1">Die Anschrift pflegen Sie unter Unternehmenseinstellungen.</p>
        </div>
        <Button onClick={save} disabled={saving} variant="secondary" className="w-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Speichern
        </Button>
      </CardContent>
    </Card>
  );
}

function BillingSection({ user }) {
  const billing = user?.tenant?.billing || {};
  const plan = billing.plan || 'free';
  const until = billing.until ? new Date(billing.until) : null;
  const daysLeft = until ? Math.max(0, Math.ceil((until - Date.now()) / 86400000)) : null;
  const PLANS = [
    { key: 'free',       name: 'Kostenlos', price: '0 €',   desc: '1 Link, 50 Bewertungen/Mo' },
    { key: 'standard',   name: 'Standard',  price: '29 €',  desc: '5 Links, unbegrenzt' },
    { key: 'premium',    name: 'Premium',   price: '79 €',  desc: '20 Links, KI-Analyse' },
    { key: 'enterprise', name: 'Enterprise',price: '199 €', desc: 'Alles, White-Label, API' },
  ];
  const statusLine = () => {
    if (billing.expired) return 'Testzeitraum abgelaufen — Sie nutzen jetzt den kostenlosen Plan';
    if (billing.payment_status === 'active' && billing.source?.startsWith('coupon:')) return until ? `Freigeschaltet bis ${until.toLocaleDateString('de-DE')}` : 'Freigeschaltet — unbefristet';
    if (billing.payment_status === 'active') return 'Aktiv · SEPA-Lastschrift';
    if (until) return `Testzeitraum · noch ${daysLeft} ${daysLeft === 1 ? 'Tag' : 'Tage'} (bis ${until.toLocaleDateString('de-DE')})`;
    return 'Testzeitraum';
  };
  const currentKey = plan === 'mvp' ? 'free' : plan;
  return (
    <div className="space-y-8">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Aktueller Plan</CardTitle>
          <CardDescription>Ihr aktives Abonnement und Zahlungsinformationen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className={`flex items-center gap-4 p-5 rounded-xl ${billing.expired ? 'bg-amber-50 border border-amber-200' : 'bg-muted/50'}`}>
            <div className="flex-1">
              <p className="font-semibold text-lg capitalize">{currentKey === 'free' ? 'Kostenlos' : currentKey}</p>
              <p className={`text-sm mt-0.5 ${billing.expired ? 'text-amber-800' : 'text-muted-foreground'}`}>{statusLine()}</p>
            </div>
            <Badge variant="outline" className="text-sm px-3 py-1">{currentKey.toUpperCase()}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            {PLANS.map(p => (
              <div key={p.key} className={`p-5 rounded-xl border-2 text-center ${p.key === currentKey ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <p className="text-xs font-bold uppercase tracking-wide mb-2">{p.name}</p>
                <p className="text-base font-bold">{p.price}<span className="text-xs font-normal text-muted-foreground">/Mo</span></p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{p.desc}</p>
                {p.key === currentKey && <Badge className="mt-3 text-[10px]">Aktuell</Badge>}
              </div>
            ))}
          </div>
          <Link to="/app/billing" className="block">
            <Button className="w-full h-11"><Zap className="w-4 h-4 mr-2" /> Plan verwalten &amp; upgraden <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </CardContent>
      </Card>
      <StripePortalCard />
      <LegalEntityCard user={user} />
      <CouponRedeemCard />
      <div className="text-xs text-muted-foreground text-center pt-2">
        Fragen?{' '}<a href="mailto:af@dwayne-advertising.de" className="underline hover:text-foreground">af@dwayne-advertising.de</a>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HVSettings() {
  const { section = 'company' } = useParams();
  const { user: authUser, loading: authLoading } = useAuth('hv_user');
  // Overlay, damit frisch gespeicherte Profiländerungen sofort überall greifen
  const [profileOverlay, setProfileOverlay] = useState(null);

  const user = profileOverlay && authUser
    ? { ...authUser, tenant: { ...authUser.tenant, public_profile: { ...authUser.tenant?.public_profile, ...profileOverlay } } }
    : authUser;

  if (authLoading) {
    return <div className="min-h-screen bg-muted/30 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const handleProfileChange = (patch) => setProfileOverlay(o => ({ ...(o || {}), ...patch }));

  const renderSection = () => {
    switch (section) {
      case 'company':   return <CompanySection user={user} />;
      case 'platforms': return <PlatformsSection user={user} />;
      case 'design':    return <DesignSection user={user} onProfileChange={handleProfileChange} />;
      case 'privacy':   return <PrivacySection user={user} />;
      case 'roles':     return <RolesSection user={user} />;
      case 'banner':    return <BannerSection user={user} />;
      case 'signature': return <SignatureSection user={user} />;
      case 'billing':   return <BillingSection user={user} />;
      default:          return <CompanySection user={user} />;
    }
  };

  const current = NAV.find(n => n.key === section) || NAV[0];

  return (
    <div className="min-h-screen bg-muted/30">
      <HVNav />
      <main className="container-main py-8">
        <div className="flex gap-8">
          <nav className="hidden md:flex flex-col gap-1 w-56 shrink-0">
            {NAV.map(item => (
              <Link key={item.key} to={`/app/settings/${item.key}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  section === item.key ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}>
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <current.icon className="w-4 h-4 text-primary" />
              </div>
              <h1 className="text-xl font-semibold">{current.label}</h1>
            </div>
            {renderSection()}
          </div>
        </div>
        <HVFooter />
      </main>
    </div>
  );
}
